import { useTranslation } from 'react-i18next';
import { GlobalItems } from '@/accessControl/GlobalItems.atom';
import { VersioningSection } from '@/accessControl/VersioningSection.molecule';
import { Checkbox } from '@/core/Checkbox.atom';
import { isLockingAndVersioningEnabled } from '@/services/systemConfiguration.utilities';
import { useWorkbookIsTopic } from '@/workbook/WorkbookIsTopic.context';
import { Icon } from '@seeqdev/qomponents';
import type { VersionState } from '@/core/hooks/useVersionState.hook';

interface AclModalLockTabProps {
  checkboxHandler: (isChecked: boolean) => void;
  understandLockingIsPermanent: boolean;
  isLocked: boolean;
  lockedTimestamp: string;
  lockedByDisplayName: string | undefined;
  userHasManagePermission: boolean;
  hasLockingFailedPreviouslyDueToGlobalItems: boolean;
  versionState: VersionState;
  /** When set, controls versioning UI; otherwise `isLockingAndVersioningEnabled()` is used. */
  includeVersioningSection?: boolean;
  isVantage?: boolean;
}

export const AclModalLockTab = ({
  checkboxHandler,
  understandLockingIsPermanent,
  isLocked,
  lockedTimestamp,
  lockedByDisplayName,
  userHasManagePermission,
  hasLockingFailedPreviouslyDueToGlobalItems,
  versionState,
  includeVersioningSection,
  isVantage,
}: AclModalLockTabProps) => {
  const { t } = useTranslation();
  const isTopic = useWorkbookIsTopic();

  const showVersioningSection = includeVersioningSection ?? isLockingAndVersioningEnabled();
  const unknown = t('UNKNOWN');
  const displayedLockedTimestamp = lockedTimestamp || unknown;
  const displayedLockedBy = lockedByDisplayName || unknown;

  const LockAnalysisModalContent = () => {
    if (userHasManagePermission && !hasLockingFailedPreviouslyDueToGlobalItems) {
      return (
        <>
          <div className="tw:p-sq-10">
            <Icon
              icon="fa-exclamation-triangle"
              type="warning"
              testId="show-error-fallback-popover"
              extraClassNames="mr5"
            />
            {isVantage
              ? t('ACCESS_CONTROL.LOCKING.WARNING_VANTAGE')
              : isTopic
                ? t('ACCESS_CONTROL.LOCKING.WARNING_TOPIC')
                : t('ACCESS_CONTROL.LOCKING.WARNING_1')}
          </div>
          <p>
            {isVantage
              ? t('ACCESS_CONTROL.LOCKING.VANTAGE_DESCRIPTION')
              : isTopic
                ? t('ACCESS_CONTROL.LOCKING.TOPIC_DESCRIPTION')
                : t('ACCESS_CONTROL.LOCKING.DESCRIPTION')}
          </p>

          {showVersioningSection && <VersioningSection versionState={versionState} />}

          <div className="tw:mt-sq-10">
            <Checkbox
              id="understand-lock-is-permanent-checkbox"
              isChecked={understandLockingIsPermanent}
              onChange={() => checkboxHandler(!understandLockingIsPermanent)}
              label={
                isVantage
                  ? 'ACCESS_CONTROL.LOCKING.VANTAGE_UNDERSTAND'
                  : isTopic
                    ? 'ACCESS_CONTROL.LOCKING.TOPIC_UNDERSTAND'
                    : 'ACCESS_CONTROL.LOCKING.UNDERSTAND'
              }
            />
          </div>
        </>
      );
    }

    if (!userHasManagePermission) {
      return (
        <>
          {isVantage
            ? t('ACCESS_CONTROL.LOCKING.INSUFFICIENT_PERMISSION_TO_LOCK_VANTAGE')
            : isTopic
              ? t('ACCESS_CONTROL.LOCKING.INSUFFICIENT_PERMISSION_TO_LOCK_TOPIC')
              : t('ACCESS_CONTROL.LOCKING.INSUFFICIENT_PERMISSION_TO_LOCK')}
        </>
      );
    }

    return null;
  };

  const LockAnalysis = (
    <div className="flexRowContainer flexFill overflowVisible tw:mt-sq-15" id="aclGrid">
      {LockAnalysisModalContent()}
      <div className="flexRowContainer justify-center tw:mt-sq-5 tw:mb-sq-10 tw:w-full">
        <GlobalItems hasLockingFailedPreviouslyDueToGlobalItems={hasLockingFailedPreviouslyDueToGlobalItems} />
      </div>
    </div>
  );

  const LockedAnalysisMetadata = (
    <div className="tw:mt-12.5" data-testid="lockSuccessMessage">
      <p>
        {isVantage
          ? t('ACCESS_CONTROL.LOCKING.IS_VANTAGE_LOCKED')
          : isTopic
            ? t('ACCESS_CONTROL.LOCKING.IS_TOPIC_LOCKED')
            : t('ACCESS_CONTROL.LOCKING.IS_LOCKED')}
      </p>
      <div>
        <b>{t('ACCESS_CONTROL.LOCKING.LOCKED_ON_DATE')}</b>
        {displayedLockedTimestamp}
      </div>
      <div>
        <b>{t('ACCESS_CONTROL.LOCKING.LOCKED_BY_USER')}</b>
        {displayedLockedBy}
      </div>
    </div>
  );

  return <>{isLocked ? LockedAnalysisMetadata : LockAnalysis}</>;
};
